import { getTranslation } from "@mivo/i18n";
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { OFFLINE_TRANSLATIONS } from "@/domains/offline/constants";
import { AxiosHeaders } from "axios";
import { persistGetResponse } from "@/domains/offline/cache/cacheWriter";
import { readCachedGet } from "@/domains/offline/cache/cacheReader";
import { getConnectivityOnline } from "@/domains/offline/connectivity/connectivityStore";
import {
  isCacheableGet,
  isOfflineClinicalMutationBlocked,
  isOfflineMutationBlocked,
  normalizeApiPath,
} from "@/domains/offline/guards/offlineCapability";
import { OfflineBlockedError } from "@/domains/offline/outbox/OfflineQueuedError";
import { queueOfflineMutation } from "@/domains/offline/sync/syncEngine";
import { getCachedAuthUser } from "@/domains/offline/repositories/authRepository";
import { authMeQueryKey } from "@/domains/shared/hooks/useAuth";
import { queryClient } from "@/domains/shared/services/queryClient";
import type { IUser } from "@/domains/shared/models/IUser";

function buildPath(config: InternalAxiosRequestConfig): string {
  const base = config.baseURL?.replace(/\/$/, "") ?? "";
  const url = config.url ?? "";
  const full = url.startsWith("http") ? url : `${base}${url.startsWith("/") ? "" : "/"}${url}`;
  return normalizeApiPath(full);
}

function paramsRecord(config: InternalAxiosRequestConfig): Record<string, unknown> | undefined {
  return config.params as Record<string, unknown> | undefined;
}

async function resolveClinicalAccessBlockedOffline(): Promise<boolean> {
  const fromQuery = queryClient.getQueryData<IUser>(authMeQueryKey);
  if (fromQuery) {
    return isOfflineClinicalMutationBlocked(fromQuery);
  }

  const cached = await getCachedAuthUser();
  return isOfflineClinicalMutationBlocked(cached);
}

export function setupOfflineInterceptors(api: AxiosInstance): void {
  api.interceptors.request.use(async (config) => {
    const method = (config.method ?? "get").toLowerCase();
    const path = buildPath(config);

    if (!getConnectivityOnline()) {
      if (method === "get" && isCacheableGet(path)) {
        const cached = await readCachedGet(path, paramsRecord(config));
        if (cached != null) {
          config.adapter = async () => ({
            data: cached,
            status: 200,
            statusText: "OK",
            headers: {},
            config,
          });
          return config;
        }
      }

      if (method !== "get" && method !== "head") {
        if (await resolveClinicalAccessBlockedOffline()) {
          throw new OfflineBlockedError(getTranslation(OFFLINE_TRANSLATIONS.clinicalAccessBlocked));
        }
        if (isOfflineMutationBlocked(method, path)) {
          throw new OfflineBlockedError(getTranslation(OFFLINE_TRANSLATIONS.actionRequiresInternet));
        }
        const body = config.data;
        const outboxId = await queueOfflineMutation(api, method, path, body);
        config.adapter = async () => ({
          data: { offlineQueued: true, outboxId },
          status: 202,
          statusText: "Accepted",
          headers: new AxiosHeaders({ "x-offline-queued": "1" }),
          config,
        });
        return config;
      }
    }

    return config;
  });

  api.interceptors.response.use(async (response) => {
    const method = (response.config.method ?? "get").toLowerCase();
    const path = buildPath(response.config);
    if (method === "get" && isCacheableGet(path)) {
      await persistGetResponse(path, response.data, paramsRecord(response.config));
    }
    return response;
  });
}
